import { Lock } from 'lucide-react';
import { sanitizeHtml } from '@/shared/lib';
import { Text } from '../text';
import css from './private-notes-panel.module.scss';

// A value is treated as rich text only when it actually contains markup. The
// column is plain `text`, and most notes are typed as plain lines (commission
// rates, closure dates, booking conditions). Running those through the HTML
// renderer would collapse every newline and turn a scannable list into one
// paragraph, so plain values render with their line breaks preserved instead.
const looksLikeHtml = (value: string): boolean => /<[a-z][\s\S]*>/i.test(value);

type PrivateNotesPanelProps = {
  notes: string | null | undefined;
  // Named so the empty state can say which record has no notes.
  emptyMessage: string;
};

/**
 * Read-only internal notes with the "not visible to customers" notice the
 * requirement mandates. Editing stays in the full edit form; this never writes.
 */
export const PrivateNotesPanel = ({
  notes,
  emptyMessage,
}: PrivateNotesPanelProps) => {
  const value = notes?.trim() ?? '';

  if (!value) {
    return (
      <div className={css.root}>
        <Text as="p" size="sm" variant="muted">
          {emptyMessage}
        </Text>
      </div>
    );
  }

  return (
    <div className={css.root}>
      <p className={css.notice}>
        <Lock className={css.noticeIcon} size={15} />
        <Text as="span" size="sm" variant="muted">
          Internal information. Not visible to customers.
        </Text>
      </p>
      {looksLikeHtml(value) ? (
        <div
          className={css.body}
          // Sanitised against the shared allowlist — paragraphs, headings,
          // lists, links and emphasis survive; scripts, handlers and unsafe
          // protocols do not.
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
        />
      ) : (
        // React escapes this; `white-space: pre-line` in the stylesheet is what
        // keeps the author's line breaks.
        <div className={css.bodyPlain}>{value}</div>
      )}
    </div>
  );
};
